import { getContext } from '@/context';
import { RECENT_SEARCH_LIMIT } from '@/consts/search';

const STORAGE_KEY = 'genai-studio-recent-searches';

const readAll = (): string[] => {
  const { internals } = getContext();
  const state = internals.getState({ permanent: true });
  const data = state[STORAGE_KEY];

  if (!Array.isArray(data)) return [];
  return data.filter(
    (term): term is string => typeof term === 'string' && term.length > 0
  );
};

const write = async (terms: string[]): Promise<void> => {
  const { internals } = getContext();
  const current = internals.getState({ permanent: true });
  await internals.setState(
    { ...current, [STORAGE_KEY]: terms.length > 0 ? terms : null },
    { permanent: true }
  );
};

const loadRecentSearches = (): string[] => {
  try {
    return readAll();
  } catch {
    return [];
  }
};

/** Most recent first, case-insensitively de-duplicated, capped at the limit. */
const addRecentSearch = async (term: string): Promise<string[]> => {
  const next = [
    term,
    ...loadRecentSearches().filter(
      (existing) => existing.toLowerCase() !== term.toLowerCase()
    ),
  ].slice(0, RECENT_SEARCH_LIMIT);

  try {
    await write(next);
  } catch {
    // Recent searches are a convenience — a failed write must not break search.
  }

  return next;
};

const removeRecentSearch = async (term: string): Promise<string[]> => {
  const next = loadRecentSearches().filter((existing) => existing !== term);

  try {
    await write(next);
  } catch {
    // See addRecentSearch.
  }

  return next;
};

const clearRecentSearches = async (): Promise<string[]> => {
  try {
    await write([]);
  } catch {
    // See addRecentSearch.
  }

  return [];
};

export {
  loadRecentSearches,
  addRecentSearch,
  removeRecentSearch,
  clearRecentSearches,
};
